// frontend/src/pages/TimerPresetPage.jsx
import { useState, useRef, useEffect } from 'react';
import { useWakeLock } from '../hooks/useWakeLock';
import { TimerAudioManager } from '../components/timer/TimerAudioManager';
import './TimerStopwatch.css';
import './HIITPage.css';

const RADIUS = 90;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function formatTime(seconds) {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function TimerPresetPage({ title, icon, accentColor, presets }) {
  const [view, setView] = useState('idle'); // idle | countdown | running | paused | done
  const [selectedMinutes, setSelectedMinutes] = useState(null);
  const [cdNum, setCdNum] = useState(3);
  const [timeLeft, setTimeLeft] = useState(0);
  const [soundsEnabled, setSoundsEnabled] = useState(true);

  const totalSecsRef = useRef(0);
  const timeLeftRef = useRef(0);
  const intervalRef = useRef(null);
  const endTimeRef = useRef(null);
  const lastBeepSecRef = useRef(null);
  const soundsEnabledRef = useRef(soundsEnabled);

  const audioRef = useRef(null);
  if (audioRef.current === null) {
    audioRef.current = new TimerAudioManager();
  }

  // Cleanup AudioManager (removes visibilitychange/touchstart listeners)
  useEffect(() => () => audioRef.current?.destroy(), []);

  useEffect(() => { soundsEnabledRef.current = soundsEnabled; }, [soundsEnabled]);

  useWakeLock(view === 'running');

  // Page Visibility re-sync
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && view === 'running' && endTimeRef.current) {
        const remainingMs = Math.max(0, endTimeRef.current - Date.now());
        setTimeLeft(Math.ceil(remainingMs / 1000));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [view]);

  // Countdown 3-2-1
  useEffect(() => {
    if (view !== 'countdown') return;
    let count = 3;
    setCdNum(3);
    if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
    const id = setInterval(() => {
      count--;
      if (count > 0) {
        setCdNum(count);
        if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
      } else {
        clearInterval(id);
        const totalMs = totalSecsRef.current * 1000;
        endTimeRef.current = Date.now() + totalMs;
        lastBeepSecRef.current = null;
        timeLeftRef.current = totalSecsRef.current;
        setTimeLeft(totalSecsRef.current);
        setView('running');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [view]);

  // Countdown timer (200ms wall-clock)
  useEffect(() => {
    if (view !== 'running') {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      const remainingMs = endTimeRef.current - Date.now();
      if (remainingMs <= 0) {
        clearInterval(intervalRef.current);
        setTimeLeft(0);
        setView('done');
        if (soundsEnabledRef.current) audioRef.current.playEndSignal();
      } else {
        const secs = Math.ceil(remainingMs / 1000);
        timeLeftRef.current = secs;
        setTimeLeft(secs);
        if (soundsEnabledRef.current && secs <= 10 && secs !== lastBeepSecRef.current) {
          lastBeepSecRef.current = secs;
          audioRef.current.playCountdownBeep();
        }
      }
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, [view]);

  async function handlePresetClick(minutes) {
    await audioRef.current.unlock();
    totalSecsRef.current = minutes * 60;
    setSelectedMinutes(minutes);
    setView('countdown');
  }

  function handlePause() {
    clearInterval(intervalRef.current);
    setView('paused');
  }

  function handleResume() {
    endTimeRef.current = Date.now() + timeLeftRef.current * 1000;
    lastBeepSecRef.current = null;
    setView('running');
  }

  function handleReset() {
    clearInterval(intervalRef.current);
    setView('countdown');
  }

  function handleAbort() {
    clearInterval(intervalRef.current);
    setView('idle');
  }

  const totalSecs = totalSecsRef.current || 1;
  const progress = timeLeft / totalSecs;
  const offset = CIRCUMFERENCE * (1 - progress);

  const isTimerMode = view !== 'idle';

  return (
    <div
      className={isTimerMode ? 'hiit-fullscreen' : undefined}
      style={isTimerMode ? { background: 'var(--color-bg-presenter)' } : undefined}
      data-theme={isTimerMode ? 'dark' : undefined}
      data-bs-theme={isTimerMode ? 'dark' : undefined}
    >
      {/* ── IDLE ── */}
      {view === 'idle' && (
        <div className="page-content">
          <h4 className="mb-4" style={{ color: 'var(--color-text)', fontWeight: 700 }}>
            <i className={`bi ${icon} me-2`} />
            {title} Quick Start
          </h4>

          <div className="tools-card">
            <div className="hiit-section-label">Dauer wählen</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
                marginBottom: 20,
              }}
            >
              {presets.map((min) => (
                <button
                  key={min}
                  className="timer-history-tile"
                  style={{
                    padding: '24px 16px',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                    minHeight: 80,
                  }}
                  onClick={() => handlePresetClick(min)}
                >
                  {min} min
                </button>
              ))}
            </div>

            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="preset_sounds_enabled"
                checked={soundsEnabled}
                onChange={(e) => setSoundsEnabled(e.target.checked)}
              />
              <label
                className="form-check-label hiit-sounds-label"
                htmlFor="preset_sounds_enabled"
              >
                <i className="bi bi-volume-up me-1" />
                Töne aktivieren
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ── COUNTDOWN ── */}
      {view === 'countdown' && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Workout abbrechen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center"></div>
            <div className="workout-control-topbar__slot"></div>
          </div>
          <div className="workout-control-content">
            <div className="hiit-countdown">
              <div className="hiit-countdown-label">Bereit machen</div>
              <div className="hiit-countdown-num">{cdNum}</div>
              <div className="hiit-countdown-cfg">
                {selectedMinutes} min {title}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── RUNNING / PAUSED ── */}
      {(view === 'running' || view === 'paused') && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Workout abbrechen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center">
              <span className="workout-control-phase-pill workout-control-phase-pill--work">
                {title}
              </span>
            </div>
            <div className="workout-control-topbar__slot"></div>
          </div>

          <div className="workout-control-content">
            <div className="timer-ring-wrapper">
              <svg width="220" height="220" viewBox="0 0 220 220">
                <circle cx="110" cy="110" r={RADIUS} fill="none" stroke="#1e293b" strokeWidth="10" />
                <circle
                  cx="110" cy="110" r={RADIUS}
                  fill="none"
                  stroke={accentColor}
                  strokeWidth="10"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  transform="rotate(-90 110 110)"
                  style={{ transition: 'stroke-dashoffset 0.2s linear' }}
                />
                <text x="110" y="118" textAnchor="middle" fontSize="32" fontFamily="Outfit, monospace" fill="#f1f5f9" fontWeight="700">
                  {formatTime(timeLeft)}
                </text>
              </svg>
            </div>
          </div>

          <div className="workout-control-bottombar">
            <button
              className="workout-control-btn-secondary"
              onClick={handleReset}
              aria-label="Reset"
            >
              <i className="bi bi-arrow-counterclockwise" />
            </button>
            <button
              className={`workout-control-btn-primary ${view === 'paused' ? 'is-paused' : 'is-playing'}`}
              onClick={view === 'paused' ? handleResume : handlePause}
              aria-label={view === 'paused' ? 'Weiter' : 'Pause'}
            >
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--pause">
                <i className="bi bi-pause-fill" />
              </span>
              <span className="workout-control-btn-primary__icon workout-control-btn-primary__icon--play">
                <i className="bi bi-play-fill" />
              </span>
            </button>
            <div
              style={{ width: 'var(--workout-control-btn-secondary-size)' }}
              aria-hidden="true"
            />
          </div>
        </>
      )}

      {/* ── DONE ── */}
      {view === 'done' && (
        <>
          <div className="workout-control-topbar">
            <div className="workout-control-topbar__slot">
              <button
                className="workout-control-btn-icon-sm"
                onClick={handleAbort}
                aria-label="Schließen"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="workout-control-topbar__slot workout-control-topbar__slot--center"></div>
            <div className="workout-control-topbar__slot"></div>
          </div>
          <div className="workout-control-content">
            <div className="hiit-done">
              <div className="hiit-done-icon">
                <svg
                  width="28" height="28" viewBox="0 0 24 24"
                  fill="none" stroke="#fff" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              </div>
              <div className="hiit-done-title">{title} abgeschlossen!</div>
              <div className="hiit-done-sub">Gut gemacht</div>
              <div className="hiit-done-stats">
                <div className="hiit-done-stat">
                  <div className="hiit-done-stat-val">{selectedMinutes} min</div>
                  <div className="hiit-done-stat-label">Dauer</div>
                </div>
              </div>
              <button
                className="workout-control-btn-fullwidth"
                onClick={handleAbort}
                style={{ maxWidth: 320, marginTop: 16 }}
              >
                <i className="bi bi-arrow-clockwise" />
                Nochmal starten
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
